/**
 * P17A-08 · Pure, catalogue-agnostic translator primitives.
 *
 * Split out of `./messages` so a Client Component can build a `Translator`
 * from a catalogue it already has in hand (received from the server, or a
 * small static default) without statically importing every registered
 * catalogue — `./messages` does that (`en.json` + `fr.json`, ~115 KB), which
 * is exactly right for server code (emails, SMS, `getTranslator()`) but was
 * getting pulled into the client bundle transitively through `./provider`,
 * shipping both catalogues to every visitor regardless of their language.
 *
 * This module has NO runtime import of any catalogue file — only a
 * type-only import of `en.json`'s shape (erased at compile time) to derive
 * `MessageKey`. It is therefore safe to import from a "use client" module.
 */

import type en from "../../messages/en.json";

import { FALLBACK_LANGUAGE } from "./config";

/** A catalogue is an arbitrarily-nested tree of strings. */
export interface MessageCatalogue {
  [key: string]: string | MessageCatalogue;
}

/**
 * The CLDR plural categories. A message that varies by count is authored as an
 * object keyed by these instead of a bare string:
 *
 * ```json
 * "itemCount": { "one": "{count} item", "other": "{count} items" }
 * ```
 *
 * Which forms a language actually uses is `Intl.PluralRules`' business, not
 * ours — English needs two, French treats 0 as singular, Arabic uses six. That
 * is exactly why `count === 1 ? "item" : "items"` can't survive translation.
 */
const PLURAL_CATEGORIES = [
  "zero",
  "one",
  "two",
  "few",
  "many",
  "other",
] as const;

function isPluralForm(node: MessageCatalogue): boolean {
  const keys = Object.keys(node);
  return (
    keys.length > 0 &&
    keys.every((k) => (PLURAL_CATEGORIES as readonly string[]).includes(k))
  );
}

/** Deep-merge `overlay` over `base`, with overlay strings winning per leaf. */
export function mergeCatalogues(
  base: MessageCatalogue,
  overlay: MessageCatalogue,
): MessageCatalogue {
  const out: MessageCatalogue = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    const existing = out[key];
    if (
      value != null &&
      typeof value === "object" &&
      existing != null &&
      typeof existing === "object"
    ) {
      out[key] = mergeCatalogues(existing, value);
    } else if (typeof value === "string" && value.length > 0) {
      // An empty string in a translation file means "not translated yet" and
      // must not shadow the fallback — otherwise the UI renders blank.
      out[key] = value;
    }
  }
  return out;
}

/**
 * Dot-separated leaf paths of a catalogue object — `"cart.empty.title"`.
 *
 * A plural node (`{ one, other }`) is itself a leaf: `"nav.itemCount"` is the
 * key, not `"nav.itemCount.one"`, since the category is chosen at runtime from
 * the count.
 */
type DotPaths<T> = {
  [K in keyof T & string]: T[K] extends string
    ? K
    : T[K] extends Record<string, string>
      ? keyof T[K] extends PluralCategory
        ? K
        : `${K}.${DotPaths<T[K]>}`
      : T[K] extends object
        ? `${K}.${DotPaths<T[K]>}`
        : never;
}[keyof T & string];

type PluralCategory = (typeof PLURAL_CATEGORIES)[number];

/** Every key the template can translate, derived from the `en` catalogue. */
export type MessageKey = DotPaths<typeof en>;

/** Values interpolated into a message's `{placeholders}`. */
export type MessageVars = Record<string, string | number>;

/**
 * Walk a dot-path to its leaf. A plural node resolves against `count` using the
 * language's own CLDR rules, falling back to `other` when the selected category
 * isn't authored (every language has `other`).
 */
function lookup(
  catalogue: MessageCatalogue,
  key: string,
  language: string,
  vars?: MessageVars,
): string | null {
  let node: string | MessageCatalogue | undefined = catalogue;
  for (const segment of key.split(".")) {
    if (node == null || typeof node === "string") return null;
    node = node[segment];
  }

  if (typeof node === "string") return node;
  if (node == null || !isPluralForm(node)) return null;

  const count = vars?.count;
  if (typeof count !== "number") {
    // A plural key used without a count can't be resolved correctly; `other` is
    // the least-wrong form rather than leaking the raw key.
    const other = node.other;
    return typeof other === "string" ? other : null;
  }

  const category = new Intl.PluralRules(language).select(count);
  const selected = node[category] ?? node.other;
  return typeof selected === "string" ? selected : null;
}

/**
 * Substitute `{name}` placeholders. Numbers are left to the caller to format —
 * a bare `String(n)` here would bypass `Intl` and print `1234.5` in a locale
 * that writes `1 234,5`. Pass pre-formatted strings from `./format` instead.
 */
function interpolate(message: string, vars?: MessageVars): string {
  if (!vars) return message;
  return message.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.hasOwn(vars, name) ? String(vars[name]) : match,
  );
}

/** A bound translator for one language. */
export type Translator = (key: MessageKey, vars?: MessageVars) => string;

/**
 * Build a translator from a catalogue already in hand — no catalogue registry,
 * no static import of any `messages/*.json`. `./messages`'s `createTranslator`
 * (server-side: resolves the catalogue from the full registry) and the client
 * `<LocaleProvider>` (resolves it from a prop) both build on this.
 *
 * A key missing from the catalogue returns the key itself — visible enough to
 * catch in review, but it should be unreachable given `MessageKey` is derived
 * from the `en` catalogue.
 */
export function createTranslatorFromCatalogue(
  catalogue: MessageCatalogue,
  language: string = FALLBACK_LANGUAGE,
): Translator {
  return (key, vars) =>
    interpolate(lookup(catalogue, key, language, vars) ?? key, vars);
}
